import breadMakerIntro from "../img/bread-maker-intro-image.png"
import {useEffect, useState} from "react";
import {RecipeIngredient} from "../domain/RcpRecipe";
import {FoodSearchPopup, getFoodCacheEntry, getFoodEntriesUsingList} from "../edit/FoodSearch";
import {fixedToHalves, safeParseNumber} from "../WebUtils";
import {FoodEntry, FoodNutrient, FoodSource, FoodType, Nutrient} from "../domain/FoodEntry";
import {CoreFoodEntryViewer} from "../NutrientView";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTrash} from "@fortawesome/free-solid-svg-icons";
import {
    CalculatedItemSet,
    convertAllFoodEntryIntoSingleEntry,
    ItemSetFoodEntryViewer
} from "../edit/NutrientTableHelper";

const whiteBreadFlourId = 1701;
const wholeBreadFlourId = 1749;
const ryeBreadFlourId = 1690;
const saltId = 6273;
const yeastId = 7848;
const waterId = 8110;
const oliveOilId = 4218;
const sugarId = 2460;
const listOfBaseFoods = [whiteBreadFlourId, wholeBreadFlourId, ryeBreadFlourId, saltId, yeastId, waterId, oliveOilId, sugarId];

let nextInternalId = 1;

export function BreadCalculator() {
    const [wholemealFlour, setWholemealFlour] = useState<number>(0);
    const [ryeFlour, setRyeFlour] = useState<number>(0);
    const [whiteFlour, setWhiteFlour] = useState<number>(100);
    const [wholemealFlourOn, setWholemealFlourOn] = useState<boolean>(false);
    const [ryeFlourOn, setRyeFlourOn] = useState<boolean>(false);
    const [whiteFlourOn, setWhiteFlourOn] = useState<boolean>(true);
    const [selectedSize, setSelectedSize] = useState<string>('small');
    const [hydrationPercentage, setHydrationPercentage] = useState<number>(60);
    const [cacheVersion, setCacheVersion] = useState<number>(0);
    const [calculatedIngredients, setCalculatedIngredients] = useState<RecipeIngredient[]>([]);
    const [extraIngredients, setExtraIngredients] = useState<RecipeIngredient[]>([]);
    const [calculatedItemSet, setCalculatedItemSet] = useState<CalculatedItemSet>(new CalculatedItemSet());

    useEffect(() => {
       getFoodEntriesUsingList(listOfBaseFoods, () => setCacheVersion(cacheVersion + 1));
    }, []);

    function wholemealFlourChange() {
        setWholemealFlourOn(!wholemealFlourOn);
        setWholemealFlour(!wholemealFlourOn ? 100 - (whiteFlour + ryeFlour ) : 0);
    }
    function ryeFlourChange() {
        setRyeFlourOn(!ryeFlourOn);
        setRyeFlour(!ryeFlourOn ? 100 - (whiteFlour + wholemealFlour ) : 0);
    }
    function whiteFlourChange() {
        setWhiteFlourOn(!whiteFlourOn);
        setWhiteFlour(!whiteFlourOn ? 100 - (ryeFlour + wholemealFlour ) : 0);
    }

    useEffect(() => {
        function sugarAmtForMode() {
            switch(selectedSize) {
                case 'small':
                case 'medium':
                case 'large': return (wholemealFlour + ryeFlour) < 15 ? 5 : 20;
                case 'pizza-med':
                case 'pizza-lge':
                case 'focaccia': return 5;
            }
            return 0.014;
        }
        function oilAmtForMode() {
            switch(selectedSize) {
                case 'small':
                case 'medium':
                case 'large': return 20;
                case 'pizza-med':
                case 'pizza-lge':
                case 'focaccia': return 25;
            }
            return 0.014;
        }
        let allEntries = [];
        allEntries.push(new RecipeIngredient(waterId, -1, totalFlourAmt() * (hydrationPercentage/100.0), "Water", nextInternalId++));
        allEntries.push(new RecipeIngredient(yeastId, -1, oilAmtForMode(), "Oil or butter", nextInternalId++));
        allEntries.push(new RecipeIngredient(saltId, -1, totalFlourAmt() * 0.016, "Salt", nextInternalId++));
        allEntries.push(new RecipeIngredient(yeastId, -1, sugarAmtForMode(), (ryeFlour + wholemealFlour) > 45 ? "Dark brown Sugar":"Light Sugar", nextInternalId++));
        if(whiteFlourOn) allEntries.push(new RecipeIngredient(whiteBreadFlourId, -1, totalFlourAmt() * (whiteFlour / 100.0), "Strong White Bread Flour", nextInternalId++));
        if(wholemealFlourOn) allEntries.push(new RecipeIngredient(wholeBreadFlourId, -1, totalFlourAmt() * (wholemealFlour / 100.0), "Strong Wholemeal Flour", nextInternalId++));
        if(ryeFlourOn) allEntries.push(new RecipeIngredient(ryeBreadFlourId, -1, totalFlourAmt() * (ryeFlour / 100.0), "Rye Flour", nextInternalId++));
        allEntries.push(new RecipeIngredient(yeastId, -1, totalFlourAmt() * 0.014, "Instant Yeast for fresh use double.", nextInternalId++));
        extraIngredients.forEach(ingredient => allEntries.push(ingredient));
        allEntries.push(new RecipeIngredient(-1, -1, 0, "", nextInternalId++));

        setCalculatedIngredients(allEntries);
        let fe = convertAllFoodEntryIntoSingleEntry(allEntries);
        setCalculatedItemSet(fe);

    }, [whiteFlourOn, wholemealFlourOn, ryeFlourOn, hydrationPercentage, cacheVersion, whiteFlour, wholemealFlour, ryeFlour, selectedSize, extraIngredients]);

    let currentTotal = whiteFlour + ryeFlour + wholemealFlour;

    function totalFlourAmt() {
        switch(selectedSize) {
            case 'small': return 360;
            case 'medium': return 500;
            case 'large': return 600;
            case 'pizza-med': return 500;
            case 'pizza-lge': return 600;
            case 'focaccia': return 500;
        }
        return 500;
    }

    function methodFromSizeAndFlour(ryeFlour: number, whiteFlour: number, selectedSize: string) {
        if(selectedSize === 'focaccia' || selectedSize.startsWith('pizza')) {
            return <ul>
                <li>Add liquid ingredients first, followed by sugar and salt, then everything else.</li>
                <li>Always add yeast last keeping it away from the salt. The easiest way to do this is make an indentation in the flour and put the yeast into it.</li>
                <li>Use the <strong>dough</strong> setting on the bread-maker to mix, kneed and prove the dough.</li>
                <li>Once the dough is ready remove from the bread-maker and let stand for a little more time.</li>
                <li>Finally, shape the dough for {selectedSize} and then allow to prove again. For loaves the dough should double in size.</li>
            </ul>;
        }

        if(ryeFlour > 30)
        return <div>
            Using more than 30% rye flour may need much longer rising time. Unless your bread-maker has a rye setting,
            you should instead consider using <strong>wholemeal flour</strong>. If you're using along with white bread
            flour, then we recommend staying below 30% rye flour.
        </div>

        return <ul>
            <li>Add liquid ingredients first, followed by sugar and salt, then everything else.</li>
            <li>Always add yeast last keeping it away from the salt. The easiest way to do this is make an indentation in the flour and put the yeast into it.</li>
            <li>Use the <strong>{whiteFlour >= 50 ? "regular" : "wholemeal"}</strong> setting on the bread-maker.</li>
            <li>Check the bake settings are right for your choice and start the process.</li>
            <li>As soon as the loaf finishes baking, remove from the tray onto a cooling rack.</li>
            <li>I often leave loaves upside down for at least a few minutes so the top stays crisp/crunchy.</li>
        </ul>;

    }

    function onChangeOfFoodEntry(e: FoodEntry, qty: number, is: RecipeIngredient) {
        const existingItem = extraIngredients.find(i => i.internalId === is.internalId);
        is.quantity = qty;
        is.foodEntryId = e.id;
        if (existingItem) {
            setExtraIngredients(extraIngredients.map(i => i.internalId === is.internalId ? is : i));
        } else {
            setExtraIngredients([...extraIngredients, is]);
        }
    }

    return <div>
        <h1>Bread Making and Dough Calculator</h1>
        <img style={{width: "250px"}} src={breadMakerIntro} alt="Portion size conversion"  />
        <form style={{fontSize: "150%"}}>
            <div className="calculationContainer">
                <h2>1. Choose which flour</h2>
                <p>Choosing the right flour for a recipe is critical. Always use high quality ingredients, especially the flour. For white and wholemeal flour choose
                strong bread making flour, good quality dark rye works well up to about 30%.</p>
            <p>
                <input type={"checkbox"} checked={wholemealFlourOn}
                       onClick={e => wholemealFlourChange()}
                       style={{width: "20px", height: "20px", cursor: "pointer"}}/>
                <input type={"text"} size={5} disabled={!wholemealFlourOn} value={wholemealFlour}
                       onChange={e => setWholemealFlour(safeParseNumber(e.target.value, 0, 0, 100 - whiteFlour + ryeFlour))}
                       style={{width: "80px", fontSize: "150%"}}/>
                <span>% Wholemeal flour</span>
            </p>
            <p><input type={"checkbox"} checked={ryeFlourOn}
                      onClick={e => ryeFlourChange()}
                      style={{width: "20px", height: "20px", cursor: "pointer"}}/>
                <input type={"text"} size={5} disabled={!ryeFlourOn} value={ryeFlour}
                       onChange={e => setRyeFlour(safeParseNumber(e.target.value, 0, 0, 100 - wholemealFlour + whiteFlour))}
                       style={{width: "80px", fontSize: "150%"}}/>
                <span>% Rye flour</span>

            </p>
            <p>
                <input type={"checkbox"} checked={whiteFlourOn}
                       onClick={e => whiteFlourChange()}
                       style={{width: "20px", height: "20px", cursor: "pointer"}}/>
                <input type={"text"} size={5} disabled={!whiteFlourOn} value={whiteFlour}
                       onChange={e => setWhiteFlour(safeParseNumber(e.target.value, 0, 0, 100 - ryeFlour + wholemealFlour))}
                       style={{width: "80px", fontSize: "150%"}}/>
                <span>% White bread or OO flour</span>
            </p>
            <p>
                {currentTotal !== 100 ? <div>Total flour so far: {currentTotal}%. Please make up to 100%</div> : ""}
            </p>
            </div>
            <div className="calculationContainer">
                <h2>2. What you're making</h2>
                <div className="foodCardContainer">
                    <div className="foodCard">
                        <h3><input type="radio" name="breadsize" value="small" checked={selectedSize === 'small'}
                                   onChange={e => setSelectedSize(e.target.value)}
                                   style={{width: "20px", height: "20px", marginBottom: "10px"}}/>
                            Small Loaf</h3>
                        <p>Perfect for 1-2 people (360g)</p>
                    </div>
                    <div className="foodCard">
                        <h3><input type="radio" name="breadsize" value="medium" checked={selectedSize === 'medium'}
                                   onChange={e => setSelectedSize(e.target.value)}
                                   style={{width: "20px", height: "20px", marginBottom: "10px"}}/>Medium
                            Loaf</h3>
                        <p>Ideal for 3-4 people (500g)</p>
                    </div>
                    <div className="foodCard">
                        <h3><input type="radio" name="breadsize" value="large" checked={selectedSize === 'large'}
                                   onChange={e => setSelectedSize(e.target.value)}
                                   style={{width: "20px", height: "20px", marginBottom: "10px"}}/>Large Loaf</h3>
                        <p>Suitable for 5+ people (600g)</p>
                    </div>
                    <div className="foodCard">
                        <h3><input type="radio" name="breadsize" value="pizza-med" checked={selectedSize === 'pizza-med'}
                                   onChange={e => setSelectedSize(e.target.value)}
                                   style={{width: "20px", height: "20px", marginBottom: "10px"}}/>Medium Pizza</h3>
                        <p>About two to three large pizzas (500g)</p>
                    </div>
                    <div className="foodCard">
                        <h3><input type="radio" name="breadsize" value="pizza-lge" checked={selectedSize === 'pizza-lge'}
                                   onChange={e => setSelectedSize(e.target.value)}
                                   style={{width: "20px", height: "20px", marginBottom: "10px"}}/>Large Pizza</h3>
                        <p>About three to four large pizzas (600g)</p>
                    </div>
                    <div className="foodCard">
                        <h3><input type="radio" name="breadsize" value="focaccia" checked={selectedSize === 'focaccia'}
                                   onChange={e => setSelectedSize(e.target.value)}
                                   style={{width: "20px", height: "20px", marginBottom: "10px"}}/>Focaccia</h3>
                        <p>One regular sized focaccia (500g)</p>
                    </div>
                </div>
            </div>
            <div className="calculationContainer">
                <h2>3. Water to flour ratio</h2>
                <p>The water to flour ratio is the amount of water compared to the dough. The most commonly used ratio is 60% water to flour. Below is a simple table:</p>
                <table>
                    <thead>
                    <tr>
                        <th>Recipe</th>
                        <th>Percentage</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td>White/regular bread and pizza</td>
                        <td>60-63%</td>
                    </tr><tr>
                        <td>Wholemeal and rye bread</td>
                        <td>63-65%</td>
                    </tr><tr>
                        <td>Focaccia</td>
                        <td>70%</td>
                    </tr>
                    </tbody>
                </table>
                <p>If you're working with wetter dough, keep your hands wet and the dough won't stick to them.</p>
                <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                    <input
                        type="range"
                        min="60"
                        max="80"
                        value={hydrationPercentage}
                        onChange={(e) => setHydrationPercentage(parseInt(e.target.value))}
                        style={{width: '200px'}}
                    />
                    <span>{hydrationPercentage}% water to flour</span>
                </div>
            </div>
            <div className="calculationContainer">
                <h2>Ingredients needed</h2>
                <div className="ingredient-list">
                    <div style={ { "flex": "1", "paddingBottom": "10px" }}>
                        <ul>
                        {calculatedIngredients.map(is => <CalculatorIngredient ingredientSet={is} onCalculatedValueChange={(e,q) => onChangeOfFoodEntry(e, q, is)}/>)}
                        </ul>
                    </div>
                    <ItemSetFoodEntryViewer itemSet={calculatedItemSet} servings={8} />
                </div>
            </div>
            <div className="calculationContainer">
                <h2>Suggested cooking method</h2>
                Always weigh and measure all ingredients carefully, consistency is key here.
                {methodFromSizeAndFlour(ryeFlour, whiteFlour, selectedSize)}
            </div>
        </form>
    </div>
}

/**
 *
 * @param ingredientSet {RecipeIngredient} the element to edit
 * @param onChange callback function passed the new element itself
 * @return {JSX.Element}
 * @constructor
 */
export function CalculatorIngredient({ingredientSet, onCalculatedValueChange}: { ingredientSet: RecipeIngredient, onCalculatedValueChange: (e: FoodEntry, q: number) => void }) {
    const [quantity] = useState<number>(ingredientSet.quantity);
    if(ingredientSet.foodEntryId === -1) {
        return <li><FoodSearchPopup value={""} onFoodSelected={fe => onCalculatedValueChange(fe, quantity)}/> Add your own extra here</li>
    }
    let foodEntry = getFoodCacheEntry(ingredientSet.foodEntryId);
    if(!foodEntry) {
        return <li>Loading...</li>;
    }

    let itemName = ingredientSet.template;
    if(itemName?.length !== 0) {
        return <li>{fixedToHalves(ingredientSet.quantity)}{foodEntry.id === waterId ? "ml" : "g"} of {itemName}</li>;
    } else {
        return <li><input size={5} style={{fontSize: "90%"}} value={fixedToHalves(ingredientSet.quantity)} onChange={e => onCalculatedValueChange(foodEntry, safeParseNumber(e.target.value, 0, 0, 10000))} />
            {foodEntry.id === waterId ? "ml" : "g"} of {foodEntry.name}
            <button><FontAwesomeIcon icon={faTrash}/></button></li>;
    }
}

